export type TemplateStatus = 'Draft' | 'Published' | 'Closed';

export type AccessMode = 'Anonymous' | 'Organization' | 'InvitedOnly';

export type FieldType =
    | 'multiple_choice'
    | 'checkboxes'
    | 'dropdown'
    | 'star_rating'
    | 'image_upload'
    | 'comment_box'
    | 'image_choice'
    | 'single_choice'
    | 'textbox';

export type TextboxInputType = 'text' | 'email' | 'phone' | 'number';

export const SUPPORTED_LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'vi', name: 'Tiếng Việt' },
    { code: 'th', name: 'ภาษาไทย' },
] as const;
export type LangCode = typeof SUPPORTED_LANGUAGES[number]['code'];

export interface FieldOption {
    id: string;
    label: string;
    labels?: Record<string, string>;
    imageUrl?: string | null;
}

export interface TemplateField {
    id: string;
    type: FieldType;
    label: string;
    labels?: Record<string, string>;
    required: boolean;
    order: number;
    placeholder?: string;
    placeholders?: Record<string, string>;
    options?: FieldOption[];
    inputType?: TextboxInputType;
    maxStars?: number;
    minValue?: number;
    maxValue?: number;
}

export interface TemplateSchema {
    fields: TemplateField[];
    languages?: string[];
    names?: Record<string, string>;
    descriptions?: Record<string, string>;
}

export interface TemplateListItem {
    id: number;
    templateCode: string;
    name: string;
    status: TemplateStatus;
    openDate: string | null;
    closeDate: string | null;
    responseCount: number;
    createdAt: string | null;
    createdBy: string | null;
    updatedAt: string | null;
}

export interface TemplatePagination {
    templates: TemplateListItem[];
    offset: number;
    limit: number;
    total: number;
    totalPages: number;
}

export interface TemplateDto {
    id: number;
    templateCode: string;
    name: string;
    description: string | null;
    status: TemplateStatus;
    schemaJson: string | null;
    openDate: string | null;
    closeDate: string | null;
    allowEditResponse: boolean;
    notifyOnPublish: boolean;
    notifyOnSubmit: boolean;
    accessMode?: AccessMode;
    createdAt: string | null;
    createdBy: string | null;
    updatedAt: string | null;
}

export interface TemplateRespondentDto {
    id: number;
    templateId: number;
    employeeId: string;
    name: string | null;
    email: string | null;
    sourceType: number;
    inviteSentAt: string | null;
    hasResponded: boolean;
    createdAt: string | null;
}

export interface TemplateRespondentPagination {
    respondents: TemplateRespondentDto[];
    offset: number;
    limit: number;
    total: number;
    totalPages: number;
}

export interface TemplateResponseDto {
    id: number;
    templateId: number;
    employeeId: string;
    employeeName: string | null;
    answersJson: string | null;
    submittedAt: string | null;
    updatedAt: string | null;
}

export interface TemplateResponsePagination {
    responses: TemplateResponseDto[];
    offset: number;
    limit: number;
    total: number;
    totalPages: number;
}

export interface CallerInfo {
    employeeId: string | null;
    name:       string | null;
    email:      string | null;
}

export interface TemplateFormData {
    template:     TemplateDto;
    myAnswersJson: string | null;
    canEdit:      boolean;
    hasSubmitted: boolean;
    isOpen:       boolean;
    caller:       CallerInfo | null;
}

export interface MyFormItem {
    templateCode: string;
    name: string;
    description: string | null;
    status: TemplateStatus;
    canEdit: boolean;
    hasSubmitted: boolean;
    openDate: string | null;
    closeDate: string | null;
    submittedAt: string | null;
    updatedAt: string | null;
}

export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
    multiple_choice: 'Multiple Choice',
    checkboxes:      'Checkboxes',
    dropdown:        'Dropdown',
    star_rating:     'Star Rating',
    image_upload:    'Image Upload',
    comment_box:     'Comment Box',
    image_choice:    'Image Choice',
    single_choice:   'Single Choice',
    textbox:         'Textbox',
};

export const CHOICE_TYPES: FieldType[] = [
    'multiple_choice', 'checkboxes', 'dropdown', 'image_choice', 'single_choice',
];

export const DEFAULT_FIELD_LABELS: Record<FieldType, string> = {
    multiple_choice: 'Multiple Choice Question',
    checkboxes:      'Checkboxes Question',
    dropdown:        'Dropdown Question',
    star_rating:     'Star Rating',
    image_upload:    'Upload Image',
    comment_box:     'Comments',
    image_choice:    'Image Choice Question',
    single_choice:   'Single Choice Question',
    textbox:         'Short Answer',
};
